import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Send } from "lucide-react";
import { listSongs, patchSong } from "@/lib/creatorApi";
import { uploadRecording } from "@/lib/recStudioApi";
import { safeApiError } from "@/lib/safeErrorText";

/** Send to a song in My Songs → it becomes that song's saved voice (replacing the one there). */
export default function SendToMySong({ makeFile, instrumental, label }) {
  const [songs, setSongs] = useState(null);
  const [id, setId] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => {
    listSongs({ limit: 100 }).then((rows) => {
      const ss = rows.filter((s) => s.audio_url);
      setSongs(ss);
      setId(ss.find((s) => s.id === instrumental?.song_id)?.id || ss[0]?.id || "");
    }).catch(() => setSongs([]));
  }, [instrumental?.song_id]);
  const song = songs?.find((s) => s.id === id);
  const send = async () => {
    setBusy(true);
    try {
      const file = await makeFile();
      const rec = await uploadRecording(file, file.name, `${song.title} — my voice (Recording Studio)`);
      const at = new Date().toISOString();
      await patchSong(song.id, { vocal: { file_url: rec.file_url, recording_id: rec.id, trim_head_sec: 0, at, label: `${song.title} — ${label}`.slice(0, 160) } });
      setDone(song);
      setSongs((all) => all.map((s) => (s.id === song.id ? { ...s, vocal: { file_url: rec.file_url } } : s)));
      toast.success(`Your voice is on “${song.title}”`);
    } catch (e) { toast.error("Couldn't send it", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  if (songs === null) return <Loader2 className="mt-4 h-5 w-5 animate-spin text-white/50" />;
  if (!songs.length) return <p className="mt-4 text-[13px] text-white/55">No songs in My Songs yet.</p>;
  return (
    <div data-testid="rec-send-song">
      <label className="mb-1 mt-3 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50" htmlFor="rec-send-song-select">Song</label>
      <select id="rec-send-song-select" data-testid="rec-send-song-select" value={id} onChange={(e) => { setId(e.target.value); setDone(null); }}
        className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white focus:border-rose-300/50 focus:outline-none">
        {songs.map((s) => <option key={s.id} value={s.id}>{s.title}{s.id === instrumental?.song_id ? " (your music)" : ""}</option>)}
      </select>
      {song?.vocal && done?.id !== song.id ? <p data-testid="rec-send-replace-warning" className="mt-3 flex gap-2 text-[12px] text-amber-200"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This song already has a saved voice — sending replaces it (the old one stays in Recordings).</p> : null}
      {song && song.id !== instrumental?.song_id ? <p className="mt-2 text-[12px] text-white/50">It lines up with this song only if the music you recorded over matches it from the start.</p> : null}
      {done ? (
        <p data-testid="rec-send-done" className="mt-4 flex items-center gap-2 text-[13px] text-emerald-200"><CheckCircle2 className="h-4 w-4" /> Sent.
          <Link to={`/create/song/${done.id}`} data-testid="rec-send-open-song" className="underline">Open the song</Link></p>
      ) : null}
      <button type="button" data-testid="rec-send-song-btn" onClick={send} disabled={busy || !song}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-fuchsia-500 px-4 py-2.5 text-[13px] font-semibold disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {busy ? "Sending…" : "Send to this song"}
      </button>
    </div>
  );
}
